<template>
    <AppPageFrame showHeader login="disallow">
        <LoadingPage v-if="invite.status == 'loading'" />

        <div v-else-if="invite.status == 'error'" class="formPage">
            <div class="notification bulmaNotification is-warning mt-4">
                <p v-if="inviteErrorMessage" style="white-space: pre-wrap" v-html="new MarkdownIt().render(inviteErrorMessage)"></p>
                <p v-else>The link you followed is invalid. Please check with your caseworker.</p>
            </div>

            <div class="is-flex is-flex-direction-column is-align-items-center">
                <SaveButton large label="Return To Home Page" @click="$router.push('/')"/>
            </div>
        </div>

        <div v-else-if="invite.status == 'ready' && form && moduleState" class="formPage">

            <!-- Header section -->
            <div v-if="currentSection">
                <h1>{{ currentSection?.name }}</h1>
                <progress v-if="currentSection != null" class="progress is-primary mt-5" :value="5 + currentSection.progress * 90" max="100"></progress>
            </div>
            
            <!-- Page: Welcome -->
            <div v-if="form.state.activePage == 'welcome'">
                <h2 class="pageHeading">Let's get you up and running!</h2>

                <div class="infoBox mt-4">
                    <p>
                        <i class="fas fa-exclamation-triangle mr-3"></i>
                        <strong>This page is for citizens under the care of <em>{{ invite.data.partnerName }}</em>.</strong>
                    </p>
                    <p class="mt-3">If you are not a citizen at {{ invite.data.partnerName }}, please ask your caseworker for the correct link.</p>
                </div>

                <p class="mt-5">This process should take around <strong>5 minutes</strong>, and then you'll be ready to apply for ID and benefits.</p>
            </div>

            <!-- Page: Terms / Waivers -->
            <div v-if="form.state.activePage == 'termsAndWaivers'">
                <h2 class="pageHeading">Please read the following important terms and conditions before using Mini City</h2>

                <Columns>
                    <div class="column termsWrapper">
                        <InformationCardField v-for="term of invite.data.terms" :key="term.url" :card="term" size="large" :viewed="moduleState.miscState.termsAndWaivers.viewedTerms.includes(term.url)" @viewed="onTermViewed(term)"/>
                    </div>
                </Columns>

                <FormCheckboxField class="mt-4" boxLabel="Check here if you agree to these terms" v-model="moduleState.miscState.termsAndWaivers.acceptedTerms"/>
            </div>

            <!-- Page: Select Site / Caseworker -->
            <div v-if="form.state.activePage == 'selectSiteAndCaseworker'">
                <h2 class="pageHeading">Please select your location and caseworker</h2>

                <HalfColumn>
                    <FormSelectField
                        label="Your Location *" :options="availableSites"
                        :allowNullSelection="Object.keys(availableSites).length > 1"
                        v-model="moduleState.miscState.selectSiteAndCaseworker.site" />
                </HalfColumn>

                <HalfColumn v-if="invite.data.partnerEnabledModules[Feature.Type.HOUSING_READINESS] == Feature.Status.ENABLED">
                    <!--<FormSelectField label="Site / Encampment *" :options="{...HousingReadinessLocations, '-- None Of The Above --': ''}"
                        v-model="moduleState.miscState.selectSiteAndCaseworker.housingReadinessLocation"/>-->
                    <FormRadioGroupField label="Are you at Pryor Street? *" :options="{'Yes': HousingReadinessLocations['Pryor Street'], 'No': ''}"
                        v-model="moduleState.miscState.selectSiteAndCaseworker.housingReadinessLocation" />
                </HalfColumn>

                <Column v-if="moduleState.miscState.selectSiteAndCaseworker.housingReadinessLocation">
                    <FormRadioGroupField label="Which kind of housing is most appropriate for you? *" :options="HousingReadinessType"
                        v-model="moduleState.miscState.selectSiteAndCaseworker.housingReadinessType" />
                </Column>

                <div class="infoBox mt-4 mb-5" v-if="moduleState.miscState.selectSiteAndCaseworker.housingReadinessLocation">
                    <p>
                        <i class="fas fa-exclamation-triangle mr-3"></i>
                        <strong>Please be sure to select the most appropriate type of housing, for the best chance of being accepted.</strong>
                    </p>
                    <p class="mt-3">Citizens who are chronically homeless, and experience disability-related barriers, should select <strong>Permanent Supportive Housing</strong>.</p>
                    <p class="mt-3">All other citizens should select <strong>Rapid Rehousing</strong>.</p>
                </div>

                <HalfColumn>
                    <FormSelectField
                        label="Your Caseworker *" :options="availableCaseworkers"
                        :allowNullSelection="Object.keys(availableCaseworkers).length > 1"
                        :isDisabled="Object.keys(availableCaseworkers).length < 1"
                        v-model="moduleState.miscState.selectSiteAndCaseworker.caseworker" />
                </HalfColumn>

                <HalfColumn v-if="Object.keys(availableAmbassadors).length > 0">
                    <FormSelectField
                        label="Your Ambassador *" :options="availableAmbassadors"
                        allowNullSelection nullSelectionLabel="-- I'm signing up without an ambassador --"
                        v-model="moduleState.miscState.selectSiteAndCaseworker.ambassador" />
                </HalfColumn>

                <HalfColumn>
                    <FormTextField label="HMIS ID (if known)" v-model="moduleState.miscState.selectSiteAndCaseworker.hmisId" trimmed/>
                </HalfColumn>
            </div>

            <!-- Page: Ambassador selfie / warning -->
            <div v-if="form.state.activePage == 'ambassadorSelfieCapture'">
                <h2 class="pageHeading">Please take a photo of yourself to verify your identity</h2>

                <DocumentUploadWidget :singleDocument="moduleState.application.verificationSelfie" @upload="uploadVerificationSelfie($event)" @delete="deleteVerificationSelfie($event)"/>

                <div class="infoBox mt-4">
                    <p>
                        <i class="fas fa-exclamation-triangle mr-3"></i>
                        <strong>Do not share any sensitive information with your ambassador!</strong>
                    </p>
                    <p class="mt-2">The ambassador is here to answer your questions, and to help if you get stuck.</p>
                    <p class="mt-2">But you should fill out the form yourself, and should not share sensitive information (like your password or Social Security number) with them.</p>
                </div>
            </div>

            <!-- Page: Username / password -->
            <div v-if="form.state.activePage == 'usernameAndPassword'">
                <h2 class="pageHeading">It's time to pick a username, and set your password!</h2>

                <div class="is-flex is-flex-direction-column is-align-items-center">
                    <FormTextField label="Username *" v-model="moduleState.application.username" trimmed autoFocus />
                    
                    <FormTextField label="Password *" isPassword v-model="moduleState.application.password">
                        <template v-slot:top>
                            <ValidationIndicator v-if="passwordValidationError" type="error" :tooltip="passwordValidationError"/>
                        </template>
                    </FormTextField>
                    
                    <FormTextField label="Repeat Password *" isPassword v-model="moduleState.miscState.usernameAndPassword.repeatPassword">
                        <template v-slot:top>
                            <ValidationIndicator v-if="repeatPasswordValidationError" type="error" :tooltip="repeatPasswordValidationError"/>
                        </template>
                    </FormTextField>

                    <LoadingSpinner v-if="usernameCheckInProgress" class="mt-4"/>
                    <SaveButton v-else large label="Proceed" @click="checkUsername()"/>
                </div>
            </div>

            <!-- Page: Basic citizen info -->
            <div v-if="form.state.activePage == 'basicCitizenInfo' && moduleState.application.profile">
                <h2 class="pageHeading">Please provide some basic information about yourself</h2>

                <Columns>
                    <FormTextField label="First Name *" v-model="moduleState.application.profile.firstName" trimmed autoFocus/>
                    <FormTextField label="Middle Name" v-model="moduleState.application.profile.middleName" trimmed/>
                    <FormTextField label="Last Name *" v-model="moduleState.application.profile.lastName" trimmed/>
                </Columns>

                <HalfColumn>
                    <FormTextField label="Your Social Security Number (if known)" v-model="moduleState.application.profile.socialSecurityNumber" trimmed :length="9" isPassword noAutoFill/>
                </HalfColumn>

                <HalfColumn>
                    <FormSelectField label="Your Sex / Gender *" :options="ProfileTypes.Sex" allowNullSelection v-model="moduleState.application.profile.sex"/>
                </HalfColumn>

                <HalfColumn>
                    <FormSelectField label="Your Race / Ethnicity *" :options="ProfileTypes.Race" allowNullSelection v-model="moduleState.application.profile.race"/>
                </HalfColumn>

                <Columns>
                    <FormDateField label="Date Of Birth *" v-model="moduleState.application.profile.dateOfBirth" past/>
                    
                    <div class="column" style="padding-top: 2.75rem">
                        <span v-if="moduleState.application.profile.dateOfBirth">
                            You are <strong>{{ formatAge(moduleState.application.profile.dateOfBirth) }}</strong> old
                        </span>
                    </div>
                </Columns>

            </div>

            <!-- Page: Contact info -->
            <div v-if="form.state.activePage == 'contactInfo' && moduleState.application.profile">
                <h2 class="pageHeading">Please share your contact information</h2>

                <HalfColumn>
                    <FormTextField label="Email Address" v-model="moduleState.application.profile.email" trimmed autoFocus/>
                </HalfColumn>

                <HalfColumn>
                    <FormTextField label="Phone Number" v-model="moduleState.application.profile.phoneNumber" trimmed />
                </HalfColumn>

                <div v-if="!moduleState.application.profile.email || moduleState.application.profile.email.length == 0" class="notification bulmaNotification is-warning mt-4">
                    <p>Without an email address, we will not be able to keep you updated about your ID and benefits applications</p>
                </div>
            </div>

            <!-- Page: Demographic categories -->
            <div v-if="form.state.activePage == 'demographicCategories'">
                <h2 class="pageHeading">Please let us know which of the following apply to you, so we can find tailored resources and support</h2>

                <CategorySelector v-model="moduleState.application.demographics" :categories="[
                    {id: 'veteran', label: 'Veteran / Active Military'},
                    {id: 'disability', label: 'Disability'},
                    {id: 'fosterChild', label: 'Foster Child'},
                    {id: 'wardOfCourt', label: 'Ward of the Court'},
                    {id: 'parent', label: 'Parent'},
                    {id: 'singleParent', label: 'Single Parent'},
                ]" />
            </div>

            <!-- Page: Needs categories -->
            <div v-if="form.state.activePage == 'needsCategories'">
                <h2 class="pageHeading">Please tell us which of the following would be most helpful for you</h2>

                <CategorySelector v-model="moduleState.application.needs" :categories="[
                    {id: 'education', label: 'Educational Resources'},
                    {id: 'employment', label: 'Employment Resources'},
                    {id: 'medical', label: 'Medical Resources'},
                    {id: 'mentalHealth', label: 'Mental Health Resources'},
                    {id: 'childcare', label: 'Childcare'},
                    {id: 'financialAid', label: 'Financial Support'},
                    {id: 'transport', label: 'Transportation Help'},
                    {id: 'internetAccess', label: 'Internet Connectivity'},
                    {id: 'banking', label: 'Banking'},
                ]" />
            </div>

            <!-- Page: Classifier questions -->
            <div v-if="form.state.activePage == 'classifierQuestions'">
                <h2 class="pageHeading">Please answer a few additional questions about your circumstances and needs</h2>

                <ClassifierQuestionField
                    v-for="question of invite.data.classifierQuestions" :key="question.id"
                    :question="question" v-model="moduleState.application.classifierQuestionAnswers[question.id]"
                    class="pt-3"/>
            </div>

            <!-- Page: HMIS Classifier questions -->
            <div v-if="form.state.activePage == 'hmisClassifierQuestions'">
                <h2 class="pageHeading">Please answer a few additional questions about your circumstances and needs</h2>

                <HmisClassifierQuestions v-model="moduleState.miscState.hmisClassifierQuestions"/>
            </div>

            <!-- Page: Equifax consent -->
            <div v-if="form.state.activePage == 'equifaxConsent'">
                <h2 class="pageHeading">Please let us know if you agree to us verifying your identity</h2>

                <p>Do we have your permission to verify your identity with Equifax?</p>
                <p>This will require us to share some of your information with them.</p>
                <br/>
                <p><strong>Verifying your identity may allow us to speed up your applications for ID and benefits.</strong></p>
                <br/>
                <p>However, you may opt-out if you'd prefer.</p>
                
                <FormRadioGroupField class="mt-3" label="Do you agree to us verifying your identity? *" :options="EquifaxConsentOptions" v-model="moduleState.miscState.equifaxConsent.consent"/>
            </div>

            <!-- Page: Signature -->
            <div v-else-if="form.state.activePage == 'signature'">
                <h2 class="pageHeading">Sign here, and we can finish creating your account!</h2>

                <HalfColumn class="mt-3">
                    <SignaturePadField class="column" style="flex-grow: 1.8" ref="signaturePad" />
                </HalfColumn>
            </div>

            <!-- Page: Pre-submit -->
            <div v-else-if="form.state.activePage == 'preSubmit'">
                <h2 class="pageHeading" style="text-align: center">Ok! We're ready to complete your registration</h2>

                <div class="is-flex is-flex-direction-column is-align-items-center">
                    <LoadingSpinner v-if="submissionInProgress" class="mt-4"/>
                    <SaveButton v-else large label="Submit!" icon="fa-paper-plane" @click="submitApplication()"/>
                </div>
            </div>

            <!-- Page: Completed confirmation -->
            <div v-else-if="form.state.activePage == 'completed'">
                <h1>You MUST click the button below to apply for ID and benefits</h1>
                <h2 class="pageHeading"><strong>Click button below to apply for ID / benefits</strong>, and to access other resources.</h2>

                <div class="is-flex is-flex-direction-column is-align-items-center">
                    <SaveButton large label="Apply For ID And Benefits" @click="$router.push('/')"/>
                </div>
            </div>

            <!-- Footer / Navigation section -->
            <div v-if="form!.state.errors.length > 0" class="notification bulmaNotification is-danger mt-4">
                <p v-for="error in form.state.errors" :key="error">{{ error }}</p>
            </div>

            <div v-if="!submissionInProgress" class="is-flex is-flex-direction-row mt-6">
                <SaveButton v-if="form.canNavigateBack()" large label="Back" icon="fa-chevron-left" @click="form.navigateBack()"/>
                <div class="is-flex-grow-1"></div>
                <SaveButton v-if="form.canNavigateForward()" large label="Next" icon="fa-chevron-right" iconRight @click="form.navigateForward()"/>
            </div>    

        </div>
    </AppPageFrame>
</template>
  
<script lang="ts" setup>
import { 
    moduleState, initNewApplication, submitModule,
    sections, type Page,
    EquifaxConsentOptions,
    PryorStreetYesNo,
    HousingReadinessLocations,
    HousingReadinessType
} from './module';

import { validateCitizenInviteToken, isUsernameAvailable } from './api';
import { CitizenInvite, InformationCard, validateAndFlattenHmisClassifiers } from './model';

import { Core, Feature, ProfileTypes } from '@minicity/core';
import { isModuleEnabled, sessionState, endSession, tryToResumeSession } from '@/core/session';
import { uploadFile } from '@/core/api';

import LoadingSpinner from '@/components/LoadingSpinner.vue';
import LoadingPage from '@/components/LoadingPage.vue';
  
import { useForm, Form, PageSpecs } from '@/composables/useForm';
import { Validations } from '@/composables/useValidation';
import { useAsync } from '@/composables/useAsync';

import { computed, onMounted, ref, watch } from 'vue';
import { formatAge, mapHttpError } from '@/core/utils';
import { notifyNetworkError } from '@/core/utils';

import InformationCardField from './components/InformationCardField.vue';
import SignaturePadField from "@/components/SignaturePadField.vue";
import CategorySelector from './components/CategorySelector.vue';
import ClassifierQuestionField from './components/ClassifierQuestionField.vue';
import HmisClassifierQuestions from './components/HmisClassifierQuestions.vue';
import ValidationIndicator from "@/components/ValidationIndicator.vue";
import { InteractionType, logFeatureInteraction } from '@/features/analytics/api';

import MarkdownIt from "markdown-it";
import FormRadioGroupField from '@/components/FormRadioGroupField.vue';
import FormSelectField from '@/components/FormSelectField.vue';

const props = defineProps<{
    inviteId: string
}>();

const invite = useAsync<CitizenInvite>();
const inviteErrorMessage = ref<string|null>(null);

/*
 * Define any special logic for each page
 */
const pages: PageSpecs<Page> = {
    "welcome": {
        nextPage: "termsAndWaivers",
    },
    "termsAndWaivers": {
        validate: ()=>{
            const state = moduleState.value!.miscState.termsAndWaivers;

            if (state.viewedTerms.length < invite.data!.terms.length) return ["Please read all of the terms and conditions"];
            if (!state.acceptedTerms)  return ["You need to accept the terms and conditions in order to continue"];

            moduleState.value!.application.agreedTermsVersion = invite.data!.termsVersion;
        },
        nextPage: "selectSiteAndCaseworker",
    },
    "selectSiteAndCaseworker": {
        validate: ()=>{
            const state = moduleState.value!.miscState.selectSiteAndCaseworker;

            if (!state.site) return ["Please select your location"];
            if (!state.caseworker) return ["Please select your caseworker"];

            if (invite.data!.partnerEnabledModules[Feature.Type.HOUSING_READINESS] == Feature.Status.ENABLED) {
                //if (state.housingReadinessLocation === null) return ["Please select your site / encampment"];
                if (state.housingReadinessLocation === null) return ["Please indicate if you are at Pryor Street"];
                moduleState.value!.application.housingReadinessLocation = state.housingReadinessLocation === "" ? undefined : state.housingReadinessLocation;

                console.log("ST", state);

                if (state.housingReadinessLocation === "") {
                    moduleState.value!.application.housingReadinessType = undefined;
                } else {
                    if (state.housingReadinessType === null) return ["Please indicate which housing type is most appropriate"];
                    moduleState.value!.application.housingReadinessType = state.housingReadinessType;
                }
            } else {
                moduleState.value!.application.housingReadinessLocation = undefined;
                moduleState.value!.application.housingReadinessType = undefined;
            }

            console.log("APPL", moduleState.value!.application);

            moduleState.value!.application.siteId = state.site.id;
            moduleState.value!.application.caseWorkerId = state.caseworker.id;
            moduleState.value!.application.assignedAmbassador = state.ambassador;
            moduleState.value!.application.hmisId = state.hmisId;
        },
        nextPage: "ambassadorSelfieCapture",
    },
    "ambassadorSelfieCapture": {
        enabled: ()=> moduleState.value!.application.assignedAmbassador != undefined,
        validate: ()=>{
            if (!moduleState.value?.application.verificationSelfie) return ["Please upload a selfie photo, to verify your identity"];
        },
        nextPage: "usernameAndPassword",
    },
    "usernameAndPassword": {},
    "basicCitizenInfo": {
        validate: ()=>{
            const profile = moduleState.value!.application.profile!;
            if (profile.firstName!.length == 0) return ["Please enter your first name"];
            if (profile.lastName!.length == 0) return ["Please enter your last name"];

            if (profile.socialSecurityNumber && profile.socialSecurityNumber.length != 0 && profile.socialSecurityNumber.length != 9)
                return ["Social Security Number should be in the form AAABBCCCC. Leave blank if not known"];

            if (!profile.dateOfBirth) return ["Please select your date of birth"];
            if (!profile.sex) return ["Please select your sex / gender"];
            if (!profile.race) return ["Please select your race / ethnicity"];
        },
        nextPage: "contactInfo",
    },
    "contactInfo": {
        validate: ()=>{
            const profile = moduleState.value!.application.profile!;
            
            if (profile.email) {
                if (profile.email.length == 0) {
                    profile.email = undefined;
                } else if (!Validations.email(profile.email)) {
                    return ["Please enter a valid email address, or leave blank"];
                }
            }

            if (profile.phoneNumber) {
                if (profile.phoneNumber.length == 0) {
                    profile.phoneNumber = undefined;
                } else if (!Validations.phone_US(profile.phoneNumber)) {
                    return ["Please enter a valid US phone number, or leave blank"];
                }
            }
        },
        nextPage: "demographicCategories",
    },
    "demographicCategories": {
        validate: ()=>{
            return [];
        },
        nextPage: "needsCategories",
    },
    "needsCategories": {
        validate: ()=>{
            return [];
        },
        nextPage: "classifierQuestions",
    },
    "classifierQuestions": {
        enabled: ()=> invite.data!.partnerEnabledModules[Feature.Type.LIFT_2_0] != Feature.Status.ENABLED,
        validate: ()=>{
            const answers = moduleState.value!.application.classifierQuestionAnswers!;
            
            for (let question of invite.data!.classifierQuestions) {
                if (answers[question.id].length > 0) {
                    if (answers[question.id].includes("Other:")) {
                        // "Other" selected, but no text provided
                        return [`Please write in your answer for the question: "${question.prompt}"`];    
                    }
                } else {
                    // No answer provided - auto-fill "prefer not to say" if that's an available option
                    if (question.answers.find(a => a.id == 'PREFER_NOT_TO_SAY')) {
                        answers[question.id] = ['PREFER_NOT_TO_SAY'];
                    } else {
                        return [`Please answer the question: "${question.prompt}"`];
                    }
                }
            }
        },
        nextPage: "hmisClassifierQuestions",
    },
    "hmisClassifierQuestions": {
        enabled: ()=> invite.data!.partnerEnabledModules[Feature.Type.LIFT_2_0] == Feature.Status.ENABLED,
        validate: ()=>{
            try {
                moduleState.value!.application.classifierQuestionAnswers = validateAndFlattenHmisClassifiers(moduleState.value!.miscState.hmisClassifierQuestions);
            } catch (ex) {
                return [`${ex}`];
            }
        },
        nextPage: "equifaxConsent",
    },
    "equifaxConsent": {
        enabled: ()=>isModuleEnabled(Feature.Type.EQUIFAX_CHECKS),
        onLoad: () => {
            const appl = moduleState.value!.application!;
            if (moduleState.value!.miscState.equifaxConsent.consent == null) {
                if (appl.equifaxConsent === true) {
                moduleState.value!.miscState.equifaxConsent.consent = EquifaxConsentOptions['Yes, my data can be shared'];
                } else if (appl.equifaxConsent === false) {
                    moduleState.value!.miscState.equifaxConsent.consent = EquifaxConsentOptions['No, do not share my data'];
                }
            }
        },
        validate: ()=>{
            const appl = moduleState.value!.application!;
            if (moduleState.value!.miscState.equifaxConsent.consent == null) return ["Please indicate whether you agree to us verifying your identity"];

            appl.equifaxConsent = moduleState.value!.miscState.equifaxConsent.consent == EquifaxConsentOptions['Yes, my data can be shared'];
        },
        nextPage: "signature"
    },
    "signature": {
        validate: ()=>{
            if ((signaturePad.value as any).isSignatureEmpty()) return ["Please provide your signature"];

            moduleState.value!.application.signature = (signaturePad.value as any).getSignatureData();
        },
        nextPage: "preSubmit"
    },
    "preSubmit": {},
    "completed": {disallowBack: true},
};


/*
 * Initialize
 */
onMounted(() => {
    if (sessionState.session || Boolean(localStorage["session"])) {
        // If we're logged-in, or there is a saved session tokem, force a logout
        endSession();
    }
    
    invite.fetch(validateCitizenInviteToken(props.inviteId))
    .then(invite => {
        startNewApplication();
    })
    .catch(err => {
        console.log(err.response)
        if (err.response.status == 401) {
            inviteErrorMessage.value = err.response.data;
        }
    });
});

const startNewApplication = () => {
    console.log("Starting new application");

    moduleState.value = initNewApplication({
        // Initial misc state
        termsAndWaivers: {
            viewedTerms: []
        },
        selectSiteAndCaseworker: {
            site: null,
            caseworker: null,
            ambassador: null,
            hmisId: null,
            housingReadinessLocation: null,
            housingReadinessEligibility: null,
        },
        equifaxConsent: {
            consent: null,
        },
        usernameAndPassword: {
            repeatPassword: ""
        },
        hmisClassifierQuestions: {}
    }, invite.data!);

    form.value = useForm(pages, sections, moduleState.value!.formNavState, onFormChanged);

    // Generate analytics event
    logFeatureInteraction("citizenOnboarding", "ONBOARDING", InteractionType.CLICK, JSON.stringify({'inviteId': props.inviteId}));
}


/*
 * Form state
 */
const form = ref<Form<Page>|null>(null);

const currentSection = computed(() => {
    if (!moduleState.value) return null;
    return form.value!.determineCurrentSection();
});

const onTermViewed = (term: InformationCard) => {
  if (moduleState.value!.miscState.termsAndWaivers.viewedTerms.includes(term.url)) return;
  moduleState.value!.miscState.termsAndWaivers.viewedTerms.push(term.url);
}

const signaturePad = ref(null);

const availableSites = computed(() => {
    if (!invite.data) return [];

    const sites:any = {};
    for (let s of invite.data.sites) {
        if (s.caseworkers.length > 0) {
            sites[s.name] = s;
        }
    }
    return sites;
});

const availableCaseworkers = computed(() => {
    if (!moduleState.value!.miscState.selectSiteAndCaseworker.site) return [];

    const caseworkers:any = {};
    for (let cw of moduleState.value!.miscState.selectSiteAndCaseworker.site.caseworkers) {
        caseworkers[cw.name] = cw;
    }
    return caseworkers;
});

const availableAmbassadors = computed(() => {
    if (!moduleState.value!.miscState.selectSiteAndCaseworker.caseworker) return [];

    const ambassadors:any = {};
    if (moduleState.value!.miscState.selectSiteAndCaseworker.caseworker.ambassadors) {
        for (let a of moduleState.value!.miscState.selectSiteAndCaseworker.caseworker.ambassadors) {
            ambassadors[a] = a;
        }
    }
    return ambassadors;
});

watch(() => moduleState.value?.miscState.selectSiteAndCaseworker.site, () => {
    const state = moduleState.value?.miscState.selectSiteAndCaseworker;
    if (!state) return;
    state.caseworker = state.site?.caseworkers.length == 1 ? state.site.caseworkers[0] : undefined;
    state.ambassador = undefined;
});

watch(() => moduleState.value?.miscState.selectSiteAndCaseworker.caseworker, () => {
    const state = moduleState.value?.miscState.selectSiteAndCaseworker;
    if (!state) return;
    state.ambassador = undefined;
});

const passwordValidationError = computed(() => {
    const minPasswordLength = 8;
    if (moduleState.value!.application.password!.length < minPasswordLength)
        return `Your password needs to be at least ${minPasswordLength} characters`;

    return null;
});

const repeatPasswordValidationError = computed(() => {
    if (moduleState.value!.application.password != moduleState.value!.miscState.usernameAndPassword.repeatPassword)
        return `Passwords do not match`;

    return null;
});

/*
 * Document uploads
 */
const deleteVerificationSelfie = (doc: Core.Document) => {
    moduleState.value!.application.verificationSelfie = undefined;
};
  
const uploadVerificationSelfie = (uploadInfo: {file: File, onDone: ()=>any}) => {
    uploadFile(uploadInfo.file, ['IMAGE'])
    .then(doc => {
        moduleState.value!.application.verificationSelfie = doc;
        moduleState.value!.formNavState.errors = [];
    })
    .catch(err => notifyNetworkError("Failed to upload selfie", err))
    .finally(() => uploadInfo.onDone());
}


/*
 * Form submission
 */
const onFormChanged = () => {
    // We could persist form state to localStorage here, to allow resuming onboarding later...
    // But that might be risky, as citizens could be using a shared device.
    // We don't want to accidently expose old incomplete form data to someone else using the device!
    // So, we'll err on the side of caution, and not support any save / resume functionality for onboarding.

    // Log analytics event
    logFeatureInteraction("citizenOnboarding", "ONBOARDING", InteractionType.PROGRESS, JSON.stringify({'inviteId': props.inviteId, 'page': form.value!.state.activePage, 'errors': form.value!.state.errors}));
};


const usernameCheckInProgress = ref<boolean>(false);

const checkUsername = async () => {
    if (moduleState.value!.application.username!.length == 0) {
        form.value!.state.errors = ["Please enter a username"];
        return;
    }
    
    if (passwordValidationError.value) {
        form.value!.state.errors = ["Please enter a valid password"];
        return;
    }

    if (repeatPasswordValidationError.value) {
        form.value!.state.errors = ["Please make sure your passwords match"];
        return;
    }

    form.value!.state.errors = [];
    console.log("Checking username", moduleState.value!.application.username);

    usernameCheckInProgress.value = true;
    try {
        const available = await isUsernameAvailable(invite.data!.id, moduleState.value!.application.username!);
        if (available) {
            form.value!.displayPage("basicCitizenInfo");
        } else {
            form.value!.state.errors = ["This username is already taken - please try another"];
        }
    } catch (ex) {
        form.value!.state.errors = [`${ex}`];
    } finally {
        usernameCheckInProgress.value = false;
    }
};


const submissionInProgress = ref<boolean>(false);

const submitApplication = async () => {
    form.value!.state.errors = [];
    console.log("Submitting", moduleState.value!.application);

    submissionInProgress.value = true;
    try {
        const response = await submitModule();
        tryToResumeSession(response.sessionId);

        form.value!.displayPage("completed");

        // Log analytics event
        logFeatureInteraction("citizenOnboarding/submit", "ONBOARDING", InteractionType.COMPLETION, JSON.stringify({'inviteId': props.inviteId}));
    } catch (ex) {
        form.value!.state.errors = [`Failed to submit - ${mapHttpError(ex)}`];

        // Log analytics event
        logFeatureInteraction("citizenOnboarding/submit", "ONBOARDING", InteractionType.ERROR, JSON.stringify({'inviteId': props.inviteId, 'errors': form.value!.state.errors}));
    } finally {
        submissionInProgress.value = false;
    }
};

</script>
  
<style scoped>
.formPage {
    max-width: 800px;
    margin: 0px auto;
    padding: 1rem;

    text-align: start;
}

.pageHeading {
    margin-top: 2rem;
    margin-bottom: 2rem;
    opacity: 0.7;
    font-size: 120%;
}

.infoBox {
    background-color: rgba(0,0,0,0.1);
    padding: 1rem;
    border-radius: 0.5rem;
}

ul {
    list-style: inside;
}

.exampleImageRow {
    text-align: center;
}

.exampleImage {
    margin: 0.75rem;
    width: min(200px, 40%);
}

.termsWrapper {
  display: inline-grid;
  align-self: center;
  align-content: center;

  justify-content: center;
  grid-gap: 1rem;

  /* Single column */
  grid-template-columns: 1fr;
  width: 100%;
}

.contentWrapper {
    max-width: min(100%, 700px);
}
</style>
